/**
 * 매출 — 본사 실매출 (월별 기본 + 기간 설정)
 *
 *  본사 실매출 = ① 크레딧 충전(PG 실결제) + ② 온라인 이용권 서비스수수료.
 *  오프라인·취소·환불은 제외.
 *  · 기본: 올해 1월~오늘 월별
 *  · 기간 설정: from/to (yyyy-MM-dd) + 프리셋(올해/최근 6·12개월)
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DollarSign, CreditCard, Percent, TrendingUp, Download } from 'lucide-react';
import { revenueApi, RevenueSummary, MonthlyRevenue, FeeRates } from '@/services/api/revenue';
import { exportToExcel, ExcelColumn } from '@/utils/excel';

const formatKrw = (n: number) => `₩${(n ?? 0).toLocaleString()}`;

/** 실효 요율(%) = 수수료/결제금액 — 소수 1자리 */
const effRate = (fee: number, base: number) =>
  base > 0 ? Math.round((fee / base) * 1000) / 10 : 0;

const toYmd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** 프리셋 기간 계산 */
function presetRange(kind: 'thisYear' | 'last6' | 'last12'): { from: string; to: string } {
  const today = new Date();
  const to = toYmd(today);
  if (kind === 'thisYear') return { from: `${today.getFullYear()}-01-01`, to };
  const months = kind === 'last6' ? 5 : 11;
  const start = new Date(today.getFullYear(), today.getMonth() - months, 1);
  return { from: toYmd(start), to };
}

export function RevenuePage({ embedded = false }: { embedded?: boolean } = {}) {
  const initial = presetRange('thisYear');
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [data, setData] = useState<RevenueSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 전사 기본 수수료율(INFO_FEE) 설정
  const [feeRates, setFeeRates] = useState<FeeRates | null>(null);
  const [pgInput, setPgInput] = useState('');
  const [svcInput, setSvcInput] = useState('');
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeMsg, setFeeMsg] = useState<string | null>(null);

  const load = useCallback((f: string, t: string) => {
    setLoading(true);
    setError(null);
    revenueApi
      .getSummary({ from: f, to: t })
      .then(setData)
      .catch((e) => setError(e?.message ?? '매출 조회에 실패했습니다.'))
      .finally(() => setLoading(false));
  }, []);

  const loadFeeRates = useCallback(() => {
    revenueApi
      .getFeeRates()
      .then((r) => {
        setFeeRates(r);
        setPgInput(r.pgFeeRate != null ? String(r.pgFeeRate) : '');
        setSvcInput(r.serviceFeeRate != null ? String(r.serviceFeeRate) : '');
      })
      .catch(() => {
        /* 요율 조회 실패는 매출 화면 전체를 막지 않는다 */
      });
  }, []);

  const saveFeeRates = () => {
    const pg = parseFloat(pgInput);
    const svc = parseFloat(svcInput);
    if (
      Number.isNaN(pg) ||
      Number.isNaN(svc) ||
      pg < 0 ||
      svc < 0 ||
      pg > 100 ||
      svc > 100
    ) {
      setFeeMsg('0~100 사이 숫자를 입력하세요.');
      return;
    }
    setFeeSaving(true);
    setFeeMsg(null);
    revenueApi
      .updateFeeRates({ pgFeeRate: pg, serviceFeeRate: svc })
      .then((r) => {
        setFeeRates(r);
        setPgInput(r.pgFeeRate != null ? String(r.pgFeeRate) : '');
        setSvcInput(r.serviceFeeRate != null ? String(r.serviceFeeRate) : '');
        setFeeMsg('저장되었습니다.');
        load(from, to); // 요율이 매출 계산에 반영되므로 재조회
      })
      .catch((e) => setFeeMsg(e?.message ?? '저장에 실패했습니다.'))
      .finally(() => setFeeSaving(false));
  };

  useEffect(() => {
    load(initial.from, initial.to);
    loadFeeRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (kind: 'thisYear' | 'last6' | 'last12') => {
    const r = presetRange(kind);
    setFrom(r.from);
    setTo(r.to);
    load(r.from, r.to);
  };

  const maxRevenue = useMemo(
    () => Math.max(1, ...(data?.months ?? []).map((m) => m.revenue)),
    [data],
  );

  const handleExport = () => {
    if (!data) return;
    const columns: ExcelColumn<MonthlyRevenue>[] = [
      { header: '월', accessor: (r) => r.ym },
      { header: '총 결제액', accessor: (r) => r.creditRevenue + r.onlineGmv },
      { header: '크레딧 총액', accessor: (r) => r.creditRevenue },
      { header: `PG수수료(추정 ${data.pgFeeRate}%)`, accessor: (r) => r.creditPgFee },
      { header: '크레딧 순매출', accessor: (r) => r.creditNet },
      { header: '온라인 결제금액(GMV)', accessor: (r) => r.onlineGmv },
      { header: '온라인 서비스수수료(본사)', accessor: (r) => r.onlineFeeRevenue },
      { header: '온라인 PG수수료(토스·사업주부담)', accessor: (r) => r.onlinePgFee },
      { header: '온라인 수수료(표준율 환산)', accessor: (r) => r.onlineFeeStandard },
      { header: '본사 실매출', accessor: (r) => r.revenue },
      { header: '본사 실매출(표준율 환산)', accessor: (r) => r.revenueStandard },
    ];
    const totalRow: MonthlyRevenue = {
      ym: '합계',
      creditRevenue: data.totalCreditRevenue,
      creditPgFee: data.totalCreditPgFee,
      creditNet: data.totalCreditNet,
      onlineFeeRevenue: data.totalOnlineFeeRevenue,
      onlineFeeStandard: data.totalOnlineFeeStandard,
      onlinePgFee: data.totalOnlinePgFee,
      onlineGmv: data.totalOnlineGmv,
      revenue: data.totalRevenue,
      revenueStandard: data.totalRevenueStandard,
    };
    exportToExcel(`매출_${data.from}_${data.to}`, '매출', columns, [...data.months, totalRow]);
  };

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
      {/* 헤더 */}
      <div>
        {!embedded && <h1 className="text-2xl font-bold text-gray-900">매출</h1>}
        <p className="mt-1 text-sm text-gray-500">
          본사 실매출 = 크레딧 순매출(PG 차감) + 온라인 이용권 서비스수수료
          {data ? ` · PG ${data.pgFeeRate}% · 서비스 ${data.serviceFeeRate}%` : ''}
          <span className="ml-1 text-gray-400">(오프라인·취소·환불 제외)</span>
        </p>
      </div>

      {/* 기간 필터 */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-col">
          <label className="mb-1 text-xs text-gray-500">시작일</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div className="flex flex-col">
          <label className="mb-1 text-xs text-gray-500">종료일</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <button
          onClick={() => load(from, to)}
          className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700"
        >
          조회
        </button>
        <div className="ml-auto flex items-center gap-2">
          <PresetBtn label="올해" onClick={() => applyPreset('thisYear')} />
          <PresetBtn label="최근 6개월" onClick={() => applyPreset('last6')} />
          <PresetBtn label="최근 12개월" onClick={() => applyPreset('last12')} />
          <button
            onClick={handleExport}
            disabled={!data || loading}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-600 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            엑셀
          </button>
        </div>
      </div>

      {/* 수수료율 설정 (INFO_FEE 전사 기본 — PG·서비스) */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="mb-2 flex items-center gap-2">
          <Percent className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-semibold text-gray-900">수수료율 설정 (전사 기본)</span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-gray-600">PG 수수료율 (%)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={pgInput}
              onChange={(e) => setPgInput(e.target.value)}
              className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex flex-col">
            <label className="mb-1 text-xs text-gray-600">서비스 수수료율 (%)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={svcInput}
              onChange={(e) => setSvcInput(e.target.value)}
              className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <button
            onClick={saveFeeRates}
            disabled={feeSaving}
            className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {feeSaving ? '저장 중…' : '요율 저장'}
          </button>
          {feeMsg && <span className="text-sm text-gray-600">{feeMsg}</span>}
          {feeRates && (feeRates.pgFeeUpdatedAt || feeRates.serviceFeeUpdatedAt) && (
            <span className="ml-auto text-xs text-gray-400">
              최종 저장{' '}
              {String(feeRates.pgFeeUpdatedAt ?? feeRates.serviceFeeUpdatedAt)
                .slice(0, 19)
                .replace('T', ' ')}
            </span>
          )}
        </div>
        <p className="mt-2 text-xs leading-relaxed text-amber-700">
          ⚠️ 이 값은 <b>전사 기본요율(INFO_FEE)</b>입니다. 저장 시점부터{' '}
          <b>온라인 이용권 정산(다음 배치)</b> · <b>크레딧 PG 차감</b> · 매출 표시에 적용됩니다. 과거
          정산 스냅샷은 바뀌지 않습니다. (PG수수료는 온라인만, 서비스수수료는 온·오프라인 모두 적용)
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="h-32 animate-pulse rounded-lg bg-gray-100" />
      ) : data ? (
        <>
          {/* KPI 카드 */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              icon={<DollarSign className="text-emerald-600" />}
              label="본사 실매출"
              value={formatKrw(data.totalRevenue)}
              sub={`표준율 환산 ${formatKrw(data.totalRevenueStandard)}`}
            />
            <KpiCard
              icon={<CreditCard className="text-blue-600" />}
              label="크레딧 순매출"
              value={formatKrw(data.totalCreditNet)}
              sub={`총액 ${formatKrw(data.totalCreditRevenue)} − PG ${formatKrw(data.totalCreditPgFee)} (추정 ${data.pgFeeRate}%)`}
            />
            <KpiCard
              icon={<Percent className="text-purple-600" />}
              label="온라인 서비스수수료"
              value={formatKrw(data.totalOnlineFeeRevenue)}
              sub={`표준율 환산 ${formatKrw(data.totalOnlineFeeStandard)}`}
            />
            <KpiCard
              icon={<TrendingUp className="text-amber-600" />}
              label="온라인 거래액(GMV)"
              value={formatKrw(data.totalOnlineGmv)}
              sub="참고 · 본사 매출 아님"
            />
          </div>

          {/* 월별 테이블 */}
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3">월</th>
                  <th className="px-4 py-3 text-right">총 결제액</th>
                  <th className="px-4 py-3 text-right">크레딧 순매출</th>
                  <th className="px-4 py-3 text-right">온라인 수수료</th>
                  <th className="px-4 py-3 text-right">본사 실매출</th>
                  <th className="px-4 py-3 w-28">비중</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.months.map((m) => (
                  <tr key={m.ym} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{m.ym}</td>
                    {/* 총 결제액 = 크레딧 충전 + 온라인 결제금액 */}
                    <td className="px-4 py-3 text-right">
                      <div className="tabular-nums text-gray-900">
                        {formatKrw(m.creditRevenue + m.onlineGmv)}
                      </div>
                      {(m.creditRevenue > 0 || m.onlineGmv > 0) && (
                        <div className="mt-0.5 text-[11px] tabular-nums text-gray-400">
                          크레딧 {formatKrw(m.creditRevenue)} · 온라인 {formatKrw(m.onlineGmv)}
                        </div>
                      )}
                    </td>
                    {/* 크레딧 순매출 (+ 총액·PG 보조) */}
                    <td className="px-4 py-3 text-right">
                      <div className="tabular-nums font-medium text-gray-900">{formatKrw(m.creditNet)}</div>
                      {m.creditRevenue > 0 && (
                        <div className="mt-0.5 text-[11px] tabular-nums text-gray-400">
                          총액 {formatKrw(m.creditRevenue)} · PG −{formatKrw(m.creditPgFee)}
                        </div>
                      )}
                    </td>
                    {/* 온라인 서비스수수료(본사 몫) + 결제금액×(PG·서비스) 계산 보조 */}
                    <td className="px-4 py-3 text-right">
                      <div className="tabular-nums text-gray-900">{formatKrw(m.onlineFeeRevenue)}</div>
                      {m.onlineGmv > 0 && (
                        <div className="mt-0.5 text-[11px] tabular-nums text-gray-400">
                          결제 {formatKrw(m.onlineGmv)} · 서비스{' '}
                          {effRate(m.onlineFeeRevenue, m.onlineGmv)}% {formatKrw(m.onlineFeeRevenue)} · PG{' '}
                          {effRate(m.onlinePgFee, m.onlineGmv)}% {formatKrw(m.onlinePgFee)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums text-gray-900">
                      {formatKrw(m.revenue)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-2 w-full rounded bg-gray-100">
                        <div
                          className="h-2 rounded bg-emerald-500"
                          style={{ width: `${Math.round((m.revenue / maxRevenue) * 100)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
                {data.months.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                      해당 기간에 매출 데이터가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr className="font-semibold text-gray-900">
                  <td className="px-4 py-3">합계</td>
                  <td className="px-4 py-3 text-right">
                    <div className="tabular-nums">
                      {formatKrw(data.totalCreditRevenue + data.totalOnlineGmv)}
                    </div>
                    <div className="mt-0.5 text-[11px] font-normal tabular-nums text-gray-400">
                      크레딧 {formatKrw(data.totalCreditRevenue)} · 온라인 {formatKrw(data.totalOnlineGmv)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="tabular-nums">{formatKrw(data.totalCreditNet)}</div>
                    {data.totalCreditRevenue > 0 && (
                      <div className="mt-0.5 text-[11px] font-normal tabular-nums text-gray-400">
                        총액 {formatKrw(data.totalCreditRevenue)} · PG −{formatKrw(data.totalCreditPgFee)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="tabular-nums">{formatKrw(data.totalOnlineFeeRevenue)}</div>
                    {data.totalOnlineGmv > 0 && (
                      <div className="mt-0.5 text-[11px] font-normal tabular-nums text-gray-400">
                        결제 {formatKrw(data.totalOnlineGmv)} · 서비스{' '}
                        {formatKrw(data.totalOnlineFeeRevenue)} · PG {formatKrw(data.totalOnlinePgFee)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatKrw(data.totalRevenue)}</td>
                  <td className="px-4 py-3" />
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="text-xs leading-relaxed text-gray-400">
            · <b>온라인</b>: 본사 매출은 <b>서비스수수료만</b>입니다. 보조줄의 PG수수료는 <b>토스 몫(사업주 정산액에서 차감)</b>이라 본사 매출이 아니며, 결제금액에 실제 적용된 요율을 참고로 표시한 값입니다. (2026-08 이전은 서비스수수료율 0% 시기라 서비스 ₩0)
            <br />· <b>크레딧</b>: 본사가 크레딧을 판매하므로 PG수수료를 본사가 부담 → 순매출(총액−PG)로 집계. PG수수료는 실제 토스 정산액이 DB에 없어 <b>설정 PG율({data.pgFeeRate}%)로 추정 차감</b>(정확값은 토스 정산 연동 시 보정).
            <br />· 온라인 수수료는 정산 배치(매일 새벽 2시) 반영 기준이라 당일 결제 일부는 다음 날 반영됩니다.
          </p>
        </>
      ) : null}
    </div>
  );
}

function PresetBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
    >
      {label}
    </button>
  );
}

function KpiCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50">{icon}</div>
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <div className="mt-3 text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-400">{sub}</div>}
    </div>
  );
}
